import {
   FC,
   SyntheticEvent,
   useEffect,
   RefObject,
   useCallback,
   useMemo,
} from "react"
import ClickAwayListener from "@mui/material/ClickAwayListener"
import Grow from "@mui/material/Grow"
import Paper from "@mui/material/Paper"
import Popper from "@mui/material/Popper"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router"
import MenuItem, { MenuItemKey, MenuItemVisibility } from "./MenuItem"
import { menuRoutes } from "./menuRoutes"
import {
   getTenantLogo,
   getTenantSupportEmail,
   getTenantSupportPhone,
} from "../Header/utils"
import { Icon, IconColor } from "../Icon"
import { PageRoutes } from "@/constants/routes"
import { AppDispatch, RootState } from "@/store"
import { logout } from "@/store/authSlice"
import "./index.scss"

interface MenuProps {
   open: boolean
   anchorRef: RefObject<HTMLButtonElement | null>
   handleClose: (e: Event | SyntheticEvent) => void
}

const Menu: FC<MenuProps> = ({ open, handleClose, anchorRef }) => {
   const dispatch = useDispatch<AppDispatch>()
   const {
      isSuperAdmin,
      role: userRole,
      tenants,
      selectedTenantId,
   } = useSelector(({ userReducer }: RootState) => userReducer)
   const navigate = useNavigate()
   const location = useLocation()
   const currentPath = useMemo(() => {
      return location.pathname
   }, [location.pathname])

   const buildId = process.env.BUILD_ID

   const tenantSupportEmail = getTenantSupportEmail(tenants, selectedTenantId)
   const tenantSupportPhone = getTenantSupportPhone(tenants, selectedTenantId)

   const handleKeyDown = useCallback(
      (e: KeyboardEvent) => {
         if (e.key === "Escape") {
            handleClose(e)
         }
      },
      [handleClose]
   )

   const handleLogout = async () => {
      try {
         await dispatch(logout())
            .unwrap()
            .then(() => {
               navigate(PageRoutes.LOGIN)
            })
      } catch (err) {
         console.error("Logout failed:", err)
      }
   }

   useEffect(() => {
      window.addEventListener("keydown", handleKeyDown)
      return () => {
         window.removeEventListener("keydown", handleKeyDown)
      }
   }, [handleKeyDown])

   return (
      <Popper
         className="menu__popper"
         open={open}
         anchorEl={anchorRef.current}
         role={undefined}
         placement="bottom-end"
         transition
         disablePortal
      >
         {({ TransitionProps, placement }) => (
            <Grow
               {...TransitionProps}
               style={{
                  transformOrigin:
                     placement === "bottom-end" ? "bottom" : "top",
               }}
            >
               <Paper
                  sx={{
                     boxShadow: "0px 1px 4px 0px rgba(0, 0, 0, 0.25)",
                     width: anchorRef.current?.offsetWidth,
                  }}
               >
                  <ClickAwayListener onClickAway={handleClose}>
                     <div
                        id="composition-menu"
                        aria-labelledby="composition-button"
                        className="menu"
                     >
                        <ul className="menu__list">
                           {menuRoutes
                              .filter((menuRoute) => {
                                 if (menuRoute.roles && userRole) {
                                    return menuRoute.roles.includes(userRole)
                                 }
                                 if (
                                    menuRoute.visibility ===
                                       MenuItemVisibility.CUSTOM &&
                                    !isSuperAdmin
                                 ) {
                                    return false
                                 }
                                 return true
                              })
                              .map((menuRoute) => (
                                 <MenuItem
                                    key={menuRoute.key}
                                    label={menuRoute.label}
                                    icon={menuRoute.icon}
                                    {...(menuRoute.route && {
                                       active:
                                          currentPath ===
                                          PageRoutes[
                                             menuRoute.route as keyof typeof PageRoutes
                                          ],
                                    })}
                                    onClick={(e) => {
                                       handleClose(e)
                                       if (menuRoute.route) {
                                          navigate(
                                             PageRoutes[
                                                menuRoute.route as keyof typeof PageRoutes
                                             ]
                                          )
                                       }
                                       if (
                                          menuRoute.key === MenuItemKey.LOGOUT
                                       ) {
                                          handleLogout()
                                       }
                                    }}
                                 />
                              ))}
                        </ul>

                        <div className="menu__support">
                           <div className="menu__support-text">
                              For support questions contact us at:
                           </div>
                           <div className="menu__support-contact">
                              <Icon
                                 name="support"
                                 color={IconColor.HONOLULU_BLUE}
                                 size="16"
                              />
                              <span>{tenantSupportPhone}</span>
                           </div>
                           <div className="menu__support-contact">
                              <Icon
                                 name="envelope"
                                 color={IconColor.HONOLULU_BLUE}
                                 size="16"
                              />
                              <a href={`mailto:${tenantSupportEmail}`}>
                                 {tenantSupportEmail}
                              </a>
                           </div>
                           <img
                              className="menu__banner"
                              src={getTenantLogo(tenants, selectedTenantId)}
                              alt="logoBanner"
                           />
                           {buildId && (
                              <div className="menu__build-id">{buildId}</div>
                           )}
                        </div>
                     </div>
                  </ClickAwayListener>
               </Paper>
            </Grow>
         )}
      </Popper>
   )
}

export default Menu
